import { Button, Card, CardContent, Grid, Paper } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import RunForm from '../../Components/BodyComponent/Experiments/RunForm';
import SkeletonComponent from '../../Components/SkeletonComponent';
import { experimentsService } from '../../Services/Experiments.services';
import styles from './ExperimentRunPage.module.css';
import { Header } from '../../Components/Shared/Header/Header';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import ExperimentEntitiesTable from '../../Components/BodyComponent/Experiments/ExperimentEntitiesTable';
import ExperimentTitle from '../../Components/BodyComponent/Experiments/ExperimentTitle';
import { UploadArea } from '../../Components/Shared/UploadArea/UploadArea';
import ExperimentBreadcrumbs from '../../Components/BodyComponent/Experiments/ExperimentBreadcrumbs';
import { shortId } from '../../Utils/Utils';
import Tooltip from '@mui/material/Tooltip';

function ExperimentRunsPage() {
    const { state } = useLocation();
    const { experimentId, runId } = useParams();
    const { isAdmin } = useSelector((state) => state.auth);
    const navigate = useNavigate();
    const [runDetails, setRunDetails] = useState(state);
    const [isRunFilesLoading, setIsRunFilesLoading] = useState(true);
    const [runFiles, setRunFiles] = useState([]);

    const [isDocumentsLoading, setIsDocumentsLoading] = useState(true);
    const [documents, setDocuments] = useState([]);

    const fetchDocuments = useCallback(async () => {
        setIsDocumentsLoading(true);

        try {
            const documents = await experimentsService.getExperimentDocuments(experimentId);
            setDocuments(documents);
        } catch (err) {
            console.error('Error fetching documents:', err);
            setDocuments([]);
        } finally {
            setIsDocumentsLoading(false);
        }
    }, [experimentId]);

    const handleDocumentDelete = async (documentId) => {
        try {
            await experimentsService.deleteExperimentDocument(experimentId, documentId);
            setDocuments((documents) => documents.filter((doc) => doc.id !== documentId));
        } catch (err) {
            console.error('Error deleting the run:', err);
        } finally {
            setIsDocumentsLoading(false);
        }
    };

    const handleViewDocument = async (documentId) => {
        try {
            const { data, contentType } = await experimentsService.downloadExperimentDocument(experimentId, documentId);
            const blob = new Blob([data], { type: contentType });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
        } catch (e) {
            console.error(e);
        }
    };

    const fetchRuns = useCallback(async () => {
        setIsRunFilesLoading(true);

        try {
            const runs = await experimentsService.getExperimentRunsResults(experimentId, runId);
            setRunFiles(runs.documents ?? []);
        } catch (err) {
            console.error('Error fetching run results:', err);
            setRunFiles([]);
        } finally {
            setIsRunFilesLoading(false);
        }
    }, [experimentId, runId]);

    useEffect(() => {
        setRunFiles([]);
        setIsRunFilesLoading(true);
        setRunDetails(state ?? null);
        fetchRuns();
        fetchDocuments();
    }, [state, fetchRuns, fetchDocuments]);

    if (!isAdmin) {
        return <Navigate to="/experiments" />;
    }

    const handleGoToEvaluation = () => {
        navigate(`/experiments/${experimentId}/evaluation`);
    };

    const handleAddRun = async () => {
        try {
            const createdRun = await experimentsService.createExperimentRun(experimentId);

            if (createdRun?.id) {
                navigate(`/experiments/${experimentId}/runs/${createdRun.id}`);
            }
        } catch (err) {
            console.error('Error creating a new run:', err);
        } finally {
            setIsRunFilesLoading(false);
        }
    };

    const handleAddDocs = () => {
        navigate(`/experiments/${experimentId}/docs`);
    };

    const handleUpdateRun = async (values) => {
        try {
            await experimentsService.updateExperimentRun(experimentId, runId, values);
            setRunDetails(values);
        } catch (err) {
            console.error('Error creating a new run:', err);
        } finally {
            setIsRunFilesLoading(false);
        }
    };

    const handleDeleteRunFile = async (documentId) => {
        try {
            await experimentsService.deleteExperimentDocument(experimentId, documentId);
            setRunFiles((runs) => runs.filter((doc) => doc.id !== documentId));
        } catch (err) {
            console.error('Error deleting the run:', err);
        } finally {
            setIsRunFilesLoading(false);
        }
    };

    const handleFileUpload = async (files) => {
        try {
            await experimentsService.uploadExperimentRunResults(experimentId, runId, files);
            fetchRuns();
            fetchDocuments();
        } catch (err) {
            console.error('Error uploading documents:', err);
        } finally {
            setIsRunFilesLoading(false);
        }
    };

    const handleRunDownload = async (row) => {
        try {
            const { data, contentType } = await experimentsService.downloadExperimentDocument(experimentId, row.id);
            const blob = new Blob([data], { type: contentType });
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', row.file_name);

            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(blobUrl);
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <Grid item xs={12} sm={12} md={12} sx={{ mt: 2, mb: 2 }}>
            <Card component={Paper}>
                <CardContent>
                    <div className="header">
                        <Header
                            title={(
                                <ExperimentBreadcrumbs
                                    crumbs={[
                                        {
                                            label: 'Experiments',
                                            href: '/experiments',
                                        },
                                        {
                                            label: (<ExperimentTitle experimentId={experimentId} />),
                                        },
                                        {
                                            label: 'Runs',
                                            href: `/experiments/${experimentId}/runs`,
                                        },
                                    ]}
                                    pageTitle={(
                                        <Tooltip
                                            style={{ cursor: 'default' }}
                                            title={`Run ID: ${runId}`}
                                        >
                                            {runDetails?.name ?? shortId(runId)}
                                        </Tooltip>
                                    )}
                                />
                            )}
                            actionButtons={[
                                (
                                    <Button
                                        key="add-run-button"
                                        variant="outlined"
                                        sx={{ ml: 2 }}
                                        onClick={handleAddRun}
                                    >
                                        Add Run
                                    </Button>
                                ),
                                (
                                    <Button
                                        key="add-docs-button"
                                        variant="outlined"
                                        sx={{ ml: 2 }}
                                        onClick={handleAddDocs}
                                    >
                                        Add Docs
                                    </Button>
                                ),
                                (
                                    <Button
                                        key="go-to-evaluation-button"
                                        variant="contained"
                                        sx={{ ml: 2 }}
                                        onClick={handleGoToEvaluation}
                                    >
                                        Evaluate
                                    </Button>
                                ),
                            ]}
                        />
                    </div>
                    {isRunFilesLoading ? (
                        <SkeletonComponent />
                    ) : (
                        <div className={styles.formAndTable}>
                            <div className={`${styles.form} ${styles.section}`}>
                                <RunForm
                                    initialValues={runDetails}
                                    onSave={handleUpdateRun}
                                />
                                <div className={styles.uploadArea}>
                                    <UploadArea
                                        onUpload={handleFileUpload}
                                        acceptedTypes={{
                                            'text/csv': ['.csv'],
                                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                                            'application/vnd.ms-excel': ['.xls'],
                                        }}
                                        text='Drag &apos;n&apos; drop some XLS/XLSX files here, or click to select files'
                                    />
                                </div>
                            </div>
                            <div className={styles.table}>
                                {runFiles.length === 0
                                    ? (
                                        <Stack sx={{ width: '100%' }} spacing={2}>
                                            <Alert severity="warning">Run results not found!</Alert>
                                        </Stack>
                                    )
                                    : isRunFilesLoading
                                        ? (
                                            <SkeletonComponent />
                                        ) : (
                                            <div className={styles.section}>
                                                <h3 className={styles.sectionHeadline}>Run Results</h3>
                                                <ExperimentEntitiesTable
                                                    documents={runFiles}
                                                    onDocumentDelete={handleDeleteRunFile}
                                                    onDownload={handleRunDownload}
                                                />
                                            </div>
                                        )}
                            </div>
                            <div className={styles.table}>
                                {isDocumentsLoading
                                    ? (
                                        <SkeletonComponent />
                                    ) : (
                                        <div className={styles.section}>
                                            <h3 className={styles.sectionHeadline}>Data Points</h3>
                                            <ExperimentEntitiesTable
                                                documents={documents}
                                                onDocumentDelete={handleDocumentDelete}
                                                onPreview={handleViewDocument}
                                            />
                                        </div>
                                    )}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </Grid>
    );
}

export default ExperimentRunsPage;
